/**
 * PropertyEvaluationWorkspace — the two-pane workspace body (spec §3.2).
 *
 * FIXED FULL-VIEWPORT WORKSPACE (owner ask, 2026-06-28, per mockup v2): this is
 * a MAP-MARKING surface, so the PAGE never scrolls. The workspace is bound to
 * AppShell's work-area viewport (the viewport-lock lives in
 * evaluate-workspace.css, scoped to `.evalws` via `:has()`): identity header
 * pinned at the top, action footer (Save bar) pinned at the bottom, the two
 * panes absorbing the height between them. The MAP is a fixed stage navigated
 * by zoom/pan only — it never scrolls. The ONLY scrollers are the side lists
 * INSIDE the panes (the AUO findings list + the photo/thumb grid) and only when
 * their content is long. See evaluate-workspace.css for the layout contract.
 *
 * It owns only CHROME state (the splitter width — remembered in localStorage,
 * the mobile record-tab, the autosave-pill mirror). All evaluation truth lives
 * in the mounted marking surface (MapPane → StaffTechnicalPanel); this component
 * writes ZERO marks and owns no save store. The footer's green "Save evaluation"
 * reaches the marking surface via the `evalws:save` window event; the pill
 * mirrors the panel's `evalws:dirty-change` events.
 *
 * TOKENS ONLY — styling lives in evaluate-workspace.css.
 */
import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../Toast';
import MapPane from './MapPane';
import ReferenceMediaPane from './ReferenceMediaPane';
import PaneSplitter from './PaneSplitter';
import type { PropertyRefLike } from '../../lib/candidatePropertyJoin';
import { requestUndo, requestRedo, subscribeHistory } from '../../lib/evalDispositionBridge';

const SPLIT_KEY = 'evalws:split-left-pct';
const SPLIT_DEFAULT = 60;
const SPLIT_MIN = 35;
const SPLIT_MAX = 75;

function readStoredSplit(): number {
  try {
    const raw = window.localStorage.getItem(SPLIT_KEY);
    if (!raw) return SPLIT_DEFAULT;
    const n = Number(raw);
    if (!Number.isFinite(n)) return SPLIT_DEFAULT;
    return Math.max(SPLIT_MIN, Math.min(SPLIT_MAX, n));
  } catch {
    return SPLIT_DEFAULT;
  }
}

export interface FloorChoice {
  floorId: string;
  label: string;
}

export default function PropertyEvaluationWorkspace({
  clientId,
  projectId,
  propertyId,
  floorId,
  title,
  floors,
  actor,
  isAdmin,
  intakeTier,
  linkedPropertyRefs,
  onFloorChange,
  onDone,
}: {
  clientId: string | null;
  projectId: string;
  propertyId: string;
  floorId: string | null;
  /** the property's display title (e.g. "Plot 14 — Ground Floor"). */
  title: string;
  /** address / projectName / clientName are still accepted from the route (the
   *  caller passes them) but no longer rendered as a subline — the property name
   *  appears ONCE in Row 1 (mockup v3.5 §B). Kept in the type for API stability. */
  address?: string;
  projectName?: string;
  clientName?: string;
  /** gridable floors for the selector (drives ?floorId). */
  floors: FloorChoice[];
  actor: { uid: string | null; name: string };
  isAdmin: boolean;
  intakeTier?: string;
  linkedPropertyRefs: PropertyRefLike[];
  onFloorChange: (floorId: string) => void;
  onDone: () => void;
}) {
  const { toast } = useToast();
  const [splitLeft, setSplitLeft] = useState<number>(readStoredSplit);
  const [mobileTab, setMobileTab] = useState<'map' | 'reference'>('map');
  // autosave pill mirror — fed by StaffTechnicalPanel's `evalws:dirty-change`.
  const [dirty, setDirty] = useState(false);
  // undo/redo availability — mirrors the marking surface's bounded mark history
  // (`evalws:history-change`). Each control disables when its stack is empty.
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  useEffect(
    () => subscribeHistory(({ canUndo: u, canRedo: r }) => {
      setCanUndo(u);
      setCanRedo(r);
    }),
    [],
  );

  // persist the splitter width (remember-width, spec §3.2).
  const onSplit = useCallback((pct: number) => {
    setSplitLeft(pct);
    try {
      window.localStorage.setItem(SPLIT_KEY, String(Math.round(pct)));
    } catch {
      /* private mode / quota — non-fatal, the width just won't persist */
    }
  }, []);

  useEffect(() => {
    function onDirtyChange(e: Event) {
      const detail = (e as CustomEvent).detail as { dirty?: boolean } | undefined;
      setDirty(!!detail?.dirty);
    }
    window.addEventListener('evalws:dirty-change', onDirtyChange);
    return () => window.removeEventListener('evalws:dirty-change', onDirtyChange);
  }, []);

  const requestSave = useCallback(() => {
    window.dispatchEvent(new CustomEvent('evalws:save'));
  }, []);

  /* ── SILENT-DISCARD GUARD (JOB 0, owner incident 2026-07-03 / VP675) ──
   * There is NO autosave in this workspace (the footer comment claimed one —
   * false; the ONLY save is the explicit button → `evalws:save`). "Back to
   * project" therefore silently discarded every unsaved mark — the exact
   * loss shape of the VP675 incident (zero floor writes ever landed while
   * the owner evaluated). The rendered save→reload path itself is proven
   * sound (test/e2e/evalWsSave.e2e.spec.ts). Two guards:
   *   1. Back-with-dirty SAVES FIRST: dispatch `evalws:save`, wait for the
   *      panel's dirty→false echo, then leave. A save failure keeps the
   *      user IN the workspace (the panel has already toasted the reason) —
   *      never a silent discard, never a dead exit.
   *   2. `beforeunload` while dirty — the browser-level close/refresh nets
   *      the same loss class. */
  const [leaving, setLeaving] = useState(false);
  const onBack = useCallback(() => {
    if (!dirty) {
      onDone();
      return;
    }
    if (leaving) return; // a save-and-leave is already in flight
    setLeaving(true);
    let settled = false;
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('evalws:dirty-change', onEcho);
      setLeaving(false);
      if (ok) {
        onDone();
      } else {
        toast(
          "Couldn't save the evaluation — fix the error shown, or press Save again. Your marks are still here.",
          {},
        );
      }
    };
    function onEcho(e: Event) {
      const detail = (e as CustomEvent).detail as { dirty?: boolean } | undefined;
      if (detail && detail.dirty === false) finish(true);
    }
    window.addEventListener('evalws:dirty-change', onEcho);
    requestSave();
    // A save that never echoes back clean within the budget = it failed
    // (the panel toasts the real reason) → stay, keep the work.
    window.setTimeout(() => finish(false), 8_000);
  }, [dirty, leaving, onDone, requestSave, toast]);

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Chrome requires returnValue to show the native prompt.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const rightPct = 100 - splitLeft;

  return (
    <div className="evalws">
      {/* ── ROW 1 — identity (the property name appears ONCE here; mockup v3.5 §B) ──
          Back · "Evaluation workspace" · single property-floor title + chip ·
          Floor dropdown · saved state. No duplicate H1, no repeated sublines. */}
      <header className="evalws-idheader">
        {/* Back SAVES FIRST when dirty (JOB 0 guard) — leaving must never
            silently discard the evaluation. */}
        <button
          type="button"
          className="evalws-btn evalws-btn-sec"
          onClick={onBack}
          disabled={leaving}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m15 18-6-6 6-6" />
          </svg>
          {leaving ? 'Saving…' : 'Back to project'}
        </button>

        <span className="evalws-eyebrow">
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 10.5 12 3l9 7.5" />
            <path d="M5 9.5V21h14V9.5" />
          </svg>
          Evaluation workspace
        </span>

        <span className="evalws-r1-div" aria-hidden="true" />

        <h1 className="evalws-id-title">
          <span className="evalws-name">{title}</span>
          <span className="evalws-chip evalws-chip-accent">Evaluating</span>
        </h1>

        <div className="evalws-id-spacer" />

        {floors.length > 0 && (
          <label className="evalws-floorsel" title="Choose the floor / surface to evaluate">
            <span className="evalws-floorsel-label" aria-hidden="true">Floor</span>
            <select
              value={floorId ?? floors[0].floorId}
              onChange={(e) => onFloorChange(e.target.value)}
              aria-label="Floor or surface to evaluate"
              style={{ border: 0, background: 'transparent', font: 'inherit', color: 'inherit', cursor: 'pointer' }}
            >
              {floors.map((f) => (
                <option key={f.floorId} value={f.floorId}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>
        )}

        <span className={`evalws-autosave${dirty ? ' is-dirty' : ''}`} role="status" aria-live="polite">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            {dirty ? <path d="M12 8v4M12 16h.01M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z" /> : <path d="M20 6 9 17l-5-5" />}
          </svg>
          {dirty ? 'Unsaved changes' : 'All changes saved'}
        </span>
      </header>

      {/* ── record-tabs (mobile only, <861px — CSS-gated) ── */}
      <div className="evalws-recordtabs" role="tablist" aria-label="Workspace view">
        <button
          type="button"
          role="tab"
          className="evalws-recordtab"
          aria-selected={mobileTab === 'map'}
          onClick={() => setMobileTab('map')}
        >
          Map
        </button>
        <button
          type="button"
          role="tab"
          className="evalws-recordtab"
          aria-selected={mobileTab === 'reference'}
          onClick={() => setMobileTab('reference')}
        >
          Reference
        </button>
      </div>

      {/* ── two panes + splitter ── */}
      <div
        className="evalws-panes"
        data-active={mobileTab}
        style={
          {
            '--evalws-left': `${splitLeft}fr`,
            '--evalws-right': `${rightPct}fr`,
          } as React.CSSProperties
        }
      >
        <MapPane
          clientId={clientId}
          projectId={projectId}
          propertyId={propertyId}
          actor={actor}
          isAdmin={isAdmin}
          intakeTier={intakeTier}
          linkedPropertyRefs={linkedPropertyRefs}
          onMsg={(m) => toast(m)}
        />
        <PaneSplitter value={splitLeft} min={SPLIT_MIN} max={SPLIT_MAX} onChange={onSplit} />
        <ReferenceMediaPane
          propertyId={propertyId}
          projectId={projectId}
          clientId={clientId}
        />
      </div>

      {/* ── action footer (sticky) — Undo/Redo (left) + Save (right) ── */}
      <footer className="evalws-footer">
        <div className="evalws-footer-history" role="group" aria-label="Undo and redo">
          <button
            type="button"
            className="evalws-btn evalws-btn-sec"
            onClick={() => requestUndo()}
            disabled={!canUndo}
            title="Undo the last mark change"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 14 4 9l5-5" />
              <path d="M4 9h11a5 5 0 0 1 5 5v1" />
            </svg>
            Undo
          </button>
          <button
            type="button"
            className="evalws-btn evalws-btn-sec"
            onClick={() => requestRedo()}
            disabled={!canRedo}
            title="Redo the last undone change"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="m15 14 5-5-5-5" />
              <path d="M20 9H9a5 5 0 0 0-5 5v1" />
            </svg>
            Redo
          </button>
        </div>
        <div className="evalws-footer-spacer" />
        {/* ONE Save (owner sign-off 2026-07-01): "Save draft" and "Save
            evaluation" were byte-identical (both → requestSave). NOTE (JOB 0
            correction, 2026-07-03): there is NO autosave — this button is the
            ONLY save trigger; the pill mirrors dirty state. The Back button
            above saves-then-leaves when dirty, so exiting can't discard. */}
        <button type="button" className="evalws-btn evalws-btn-commit" onClick={requestSave}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
            <path d="M17 21v-8H7v8" />
            <path d="M7 3v5h8" />
          </svg>
          Save
        </button>
      </footer>
    </div>
  );
}
